/**
 * One game, played (ADR 0075): what the run is, the score and the clock, the game, the board.
 *
 * The core owns the run: it starts it, ends it, sends it and shows the verdict. The game
 * only plays, through the registry, and never calls the API. The score is the large figure
 * here, not the clock: a game is ranked on its score, and time only breaks ties.
 *
 * There is no pause (CLAUDE.md §10): leaving the page, or this route, sends the run as it
 * stands, so closing the tab ends a run rather than losing it.
 */
import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getGame, type GameHandle, type GameStatus, type RunLog } from "@/games/registry";
import { useTenant } from "@/core/tenant/TenantProvider";
import { formatDuration } from "@/core/play/time";
import { Button } from "@/core/ui";
import {
  finishRunOnLeave,
  playMove,
  useFinishRun,
  useGameBoard,
  useGames,
  useStartRun,
  type RunView,
  type StartedRun,
} from "./api";
import { useNumber } from "./format";

type Phase = "intro" | "playing" | "sending" | "result" | "send_error";

export function GameRunPage() {
  const { key = "" } = useParams();
  const { t } = useTranslation();
  const tenant = useTenant();
  // A game the tenant did not pick does not exist here, as on the server.
  const module = tenant.enabled_games.includes(key) ? getGame(key) : undefined;
  if (tenant.enabled_games.length === 0)
    return <p className="bt-page-message">{t("games.disabled")}</p>;
  if (!module) return <p className="bt-page-message">{t("games.unknown")}</p>;
  return <GameRun gameKey={key} />;
}

function GameRun({ gameKey }: { gameKey: string }) {
  const { t } = useTranslation();
  const number = useNumber();
  const module = getGame(gameKey)!;
  const ns = module.namespace;
  const [searchParams] = useSearchParams();
  const mode: "daily" | "free" = searchParams.get("mode") === "free" ? "free" : "daily";

  const hub = useGames();
  const summary = hub.data?.games.find((game) => game.key === gameKey);
  const start = useStartRun(gameKey);
  const finish = useFinishRun();

  const [phase, setPhase] = useState<Phase>("intro");
  const [run, setRun] = useState<StartedRun | null>(null);
  const [startedAt, setStartedAt] = useState(0);
  const [status, setStatus] = useState<GameStatus>({ score: 0, over: false, stats: {} });
  const [verdict, setVerdict] = useState<RunView | null>(null);
  const [pending, setPending] = useState<RunLog | null>(null);
  const handleRef = useRef<GameHandle | null>(null);
  const headerRef = useRef<HTMLElement | null>(null);

  // Read by the leave handlers, which outlive any one render.
  const live = useRef<{ phase: Phase; run: StartedRun | null }>({ phase, run });
  live.current = { phase, run };

  const send = useCallback(
    (runId: number, log: RunLog) => {
      setPending(log);
      setPhase("sending");
      finish.mutate(
        { id: runId, run: log },
        {
          onSuccess: (result) => {
            setVerdict(result);
            setPhase("result");
          },
          onError: () => setPhase("send_error"),
        },
      );
    },
    [finish],
  );

  const begin = () => {
    start.mutate(mode, {
      onSuccess: (started) => {
        handleRef.current = null;
        setRun(started);
        setStartedAt(Date.now());
        setStatus({ score: 0, over: false, stats: {} });
        setVerdict(null);
        setPhase("playing");
        // On a phone the start button sits below the rules, which now go: bring the game up,
        // clear of the sticky app header.
        const header = headerRef.current;
        const shell = document.querySelector<HTMLElement>(".bt-app__header");
        const top = header ? header.getBoundingClientRect().top - (shell?.offsetHeight ?? 0) : 0;
        if (top < 0) window.scrollBy({ top });
      },
    });
  };

  const runId = run?.id;
  const play = useCallback(
    (move: string) =>
      runId === undefined ? Promise.reject(new Error("no run")) : playMove(runId, move),
    [runId],
  );

  const endRun = () => {
    if (!run || !handleRef.current) return;
    send(run.id, handleRef.current.end());
  };

  const onOver = useCallback(
    (log: RunLog) => {
      const current = live.current.run;
      if (current && live.current.phase === "playing") send(current.id, log);
    },
    [send],
  );

  // Leaving mid-run sends it as it stands: by closing the tab, or by navigating away.
  useEffect(() => {
    const leave = () => {
      const { phase: now, run: current } = live.current;
      if (now === "playing" && current && handleRef.current) {
        live.current = { phase: "sending", run: current };
        finishRunOnLeave(current.id, handleRef.current.end());
      }
    };
    window.addEventListener("pagehide", leave);
    return () => {
      window.removeEventListener("pagehide", leave);
      leave();
    };
  }, []);

  const playing = phase === "playing";
  const showBoard = phase !== "intro" && run !== null;
  const kind = run
    ? run.day === null
      ? t("games.kind_free")
      : run.is_ranked
        ? t("games.kind_daily_ranked")
        : t("games.kind_daily_replay")
    : null;
  const replay = mode === "daily" && Boolean(summary?.my_ranked_run);

  return (
    <div className="bt-game-run">
      <div className="bt-game-run__main">
        <header className="bt-game-run__header" ref={headerRef}>
          <Link to="/games" className="bt-game-run__back">
            {t("games.back")}
          </Link>
          <div className="bt-game-run__headline">
            <div className="bt-game-run__titles">
              <h1 className="bt-page-title bt-game-run__title">{t("name", { ns })}</h1>
              <p className="bt-game-run__kind">{kind ?? t("tagline", { ns })}</p>
            </div>
            {phase !== "intro" ? (
              <div className="bt-game-run__score" aria-live="off">
                <span className="bt-game-run__score-label">{t("games.score")}</span>
                <span className="bt-game-run__score-value">
                  {number(verdict?.score ?? status.score)}
                </span>
                <RunClock
                  startedAt={startedAt}
                  frozenMs={playing ? null : (verdict?.duration_ms ?? null)}
                  running={playing}
                />
              </div>
            ) : null}
          </div>
        </header>

        {phase === "intro" ? (
          <Intro
            ns={ns}
            notice={
              mode === "free"
                ? t("games.free_notice")
                : replay
                  ? t("games.replay_notice")
                  : t("games.ranked_notice")
            }
            action={
              mode === "free"
                ? t("games.start_free")
                : replay
                  ? t("games.play_again")
                  : t("games.play_daily")
            }
            loading={start.isPending}
            onStart={begin}
          />
        ) : null}

        {showBoard ? (
          <Suspense fallback={<p className="bt-page-message">{t("app.loading")}</p>}>
            <module.Player
              key={run.id}
              seed={run.seed ?? null}
              view={run.view ?? null}
              play={play}
              handleRef={handleRef}
              onStatus={setStatus}
              onOver={onOver}
            />
          </Suspense>
        ) : null}

        {playing ? (
          <div className="bt-game-run__actions">
            <Button variant="secondary" onClick={endRun}>
              {t("games.end_run")}
            </Button>
          </div>
        ) : null}
        {phase === "sending" ? (
          <p className="bt-game-run__status" role="status">
            {t("games.sending")}
          </p>
        ) : null}
        {phase === "send_error" && run && pending ? (
          <div className="bt-game-run__verdict bt-game-run__verdict--error" role="alert">
            <p>{t("games.send_error")}</p>
            <Button variant="secondary" onClick={() => send(run.id, pending)}>
              {t("games.retry")}
            </Button>
          </div>
        ) : null}
        {phase === "result" && verdict ? (
          <Verdict
            verdict={verdict}
            players={summary?.players_today}
            onAgain={begin}
            again={start.isPending}
          />
        ) : null}
      </div>

      <aside className="bt-game-run__aside">
        <DayBoard gameKey={gameKey} />
        <div className="bt-game-run__best">
          <h2 className="bt-game-run__aside-title">{t("games.personal_best")}</h2>
          {summary?.personal_best ? (
            <p className="bt-game-run__best-score">{number(summary.personal_best.score)}</p>
          ) : (
            <p className="bt-game-run__quiet">{t("games.personal_best_none")}</p>
          )}
        </div>
      </aside>
    </div>
  );
}

function Intro({
  ns,
  notice,
  action,
  loading,
  onStart,
}: {
  ns: string;
  notice: string;
  action: string;
  loading: boolean;
  onStart: () => void;
}) {
  const { t } = useTranslation();
  const keys = t("keys", { ns, returnObjects: true }) as Record<string, string> | string;
  const controls = t("controls", { ns, returnObjects: true }) as Record<string, string> | string;
  return (
    <div className="bt-game-run__intro">
      <p className="bt-game-run__rules">{t("rules", { ns })}</p>
      {typeof keys === "object" && typeof controls === "object" ? (
        <section aria-labelledby="game-controls">
          <h2 id="game-controls" className="bt-game-run__aside-title">
            {t("controls_title", { ns })}
          </h2>
          <dl className="bt-game-run__keys">
            {Object.entries(keys).map(([name, key]) => (
              <div key={name}>
                <dt>{controls[name] ?? name}</dt>
                <dd>
                  <kbd>{key}</kbd>
                </dd>
              </div>
            ))}
          </dl>
        </section>
      ) : null}
      <p className="bt-game-run__notice">{notice}</p>
      <div className="bt-game-run__actions">
        <Button onClick={onStart} loading={loading}>
          {action}
        </Button>
      </div>
    </div>
  );
}

function Verdict({
  verdict,
  players,
  onAgain,
  again,
}: {
  verdict: RunView;
  players: number | undefined;
  onAgain: () => void;
  again: boolean;
}) {
  const { t } = useTranslation();
  if (verdict.status === "rejected") {
    const reason = verdict.rejected_reason === "clock_mismatch" ? "clock_mismatch" : "other";
    return (
      <div className="bt-game-run__verdict bt-game-run__verdict--error" role="alert">
        <h2 className="bt-game-run__verdict-title">{t("games.rejected_title")}</h2>
        <p>{t(`games.rejected.${reason}`)}</p>
        <VerdictActions onAgain={onAgain} again={again} />
      </div>
    );
  }
  return (
    <div className="bt-game-run__verdict" role="status">
      <h2 className="bt-game-run__verdict-title">{t("games.result_title")}</h2>
      <p>
        {verdict.rank
          ? t("games.result_rank", {
              rank: verdict.rank,
              count: Math.max(players ?? 0, verdict.rank),
            })
          : t("games.result_unranked")}
      </p>
      <VerdictActions onAgain={onAgain} again={again} />
    </div>
  );
}

function VerdictActions({ onAgain, again }: { onAgain: () => void; again: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="bt-game-run__actions">
      <Button onClick={onAgain} loading={again}>
        {t("games.play_again")}
      </Button>
      <Link to="/games" className="bt-button bt-button--secondary bt-button--md">
        {t("games.back")}
      </Link>
    </div>
  );
}

function RunClock({
  startedAt,
  frozenMs,
  running,
}: {
  startedAt: number;
  frozenMs: number | null;
  running: boolean;
}) {
  const { t } = useTranslation();
  const [now, setNow] = useState(() => Date.now());
  const [stoppedAt, setStoppedAt] = useState<number | null>(null);
  useEffect(() => {
    if (!running) {
      setStoppedAt((value) => value ?? Date.now());
      return;
    }
    setStoppedAt(null);
    const id = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(id);
  }, [running]);
  // The client's reading is cosmetic; once sent, the server's game time replaces it.
  const ms = frozenMs ?? Math.max(0, (stoppedAt ?? now) - startedAt);
  return (
    <span className="bt-game-run__clock" aria-label={`${t("games.time")} ${formatDuration(ms)}`}>
      {formatDuration(ms)}
    </span>
  );
}

function DayBoard({ gameKey }: { gameKey: string }) {
  const { t } = useTranslation();
  const number = useNumber();
  const board = useGameBoard(gameKey);
  return (
    <section aria-labelledby="game-board" className="bt-game-run__board">
      <div className="bt-game-run__board-head">
        <h2 id="game-board" className="bt-game-run__aside-title">
          {t("games.board_title")}
        </h2>
        {board.data ? (
          <span className="bt-game-run__players">
            {t("games.players", { count: board.data.players })}
          </span>
        ) : null}
      </div>
      {board.data && board.data.entries.length === 0 ? (
        <p className="bt-game-run__quiet">{t("games.board_empty")}</p>
      ) : null}
      {board.data && board.data.entries.length > 0 ? (
        <ol className="bt-game-board">
          {board.data.entries.slice(0, 10).map((entry) => (
            <li
              key={`${entry.rank}-${entry.participant}`}
              className="bt-game-board__row"
              aria-current={entry.is_me ? "true" : undefined}
            >
              <span className="bt-game-board__rank">{entry.rank}</span>
              <span className="bt-game-board__name">
                {entry.is_me ? t("games.you") : entry.participant}
              </span>
              <span className="bt-game-board__score">{number(entry.score)}</span>
              <span className="bt-game-board__time">{formatDuration(entry.duration_ms)}</span>
            </li>
          ))}
        </ol>
      ) : null}
      <p className="bt-game-run__note">{t("games.board_note")}</p>
    </section>
  );
}
